import { Fragment, useEffect } from 'react';

import {
  FileData,
  STATUS_FILE,
  useHelpfindUploady,
} from '@/hooks/useHelpfindUploady';

import { useAttachmentQuery } from '@/api/getters';
import { validFileTypes } from '@/components/helpers/isValidFile';
import Instruction, { InstructionProps } from '@/components/Instruction';
import { Skeleton } from '@/components/ui/Skeleton';
import { API_URL } from '@/consts';
import { queryClient } from '@/lib/query';
import cn from '@/utils/cn';

import { Hint } from '../Hint';
import FileItem from './FileItem';
import { UploaderDropzone } from './UploaderDropzone';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { Trans, useLingui } from '@lingui/react';
import ChunkedUploady from '@rpldy/chunked-uploady';
import { retryEnhancer } from '@rpldy/retry-hooks';
import { Controller, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

export interface UploaderProps {
  instructionProps: InstructionProps;
  title: string;
  required?: boolean;
  inputFieldName: string;
  attachmentTypeId: number;
  triggerOnMount?: boolean;
  hint?: { title: string; description: string };
  extraValidate?: (files: FileData[]) => string | undefined;
  disabled?: boolean;
}

export const Uploader = (props: UploaderProps) => {
  const { id: hash } = useParams<{ id: string }>();

  return (
    <ChunkedUploady
      method="POST"
      destination={{
        url: `${API_URL}/api/self-service/${hash}/attachments`,
        params: {
          attachment_type_id: props.attachmentTypeId,
        },
        headers: {
          accept: 'application/json',
          pragma: 'no-cache',
          cacheControl: 'no-cache',
        },
        filesParamName: 'file',
      }}
      concurrent
      maxConcurrent={3}
      chunkSize={1048576}
      inputFieldName={props.inputFieldName}
      multiple
      autoUpload={false}
      accept={validFileTypes.join(',')}
      enhancer={retryEnhancer}
      fileInputId={`uploader-${props.inputFieldName}`}
      data-test="uploader"
    >
      <UploaderPrimitive {...props} />
    </ChunkedUploady>
  );
};
const UploaderPrimitive = ({
  title,
  instructionProps,
  inputFieldName,
  required = false,
  attachmentTypeId,
  hint,
  extraValidate,
  disabled = false,
}: UploaderProps) => {
  const { i18n } = useLingui();
  const { resetField } = useFormContext();
  const { id: hash } = useParams<{ id: string }>();
  const { data: attachments, isLoading } = useAttachmentQuery(attachmentTypeId);

  const {
    files,
    handleUpdatePassword,
    handleDeleteFile,
    handleRetry,
    ButtonFile,
    error,
    warning,
  } = useHelpfindUploady({
    defaultValue: Array.isArray(attachments)
      ? attachments?.map((item) => ({
          ...item,
          id: item.id?.toString(),
          status:
            item.status && item.status === STATUS_FILE.ERROR
              ? STATUS_FILE.ERROR
              : STATUS_FILE.FINISHED,
          progress: 100,
          locked: item.can_be_managed ? !item.can_be_managed : false,
          downloadUrl: item.download_url,
          passwordProtected: !!item.password,
        }))
      : [],
    multiple: true,
    onFinish: (batch) => {
      setTimeout(() => {
        queryClient.setQueryData(
          ['attachments', hash, attachmentTypeId],
          () => {
            return [...batch];
          }
        );
      }, 100); // 3000 milliseconds was tested mobile
    },
  });
  const [parent] = useAutoAnimate();
  const extraError = extraValidate?.(files);

  useEffect(() => {
    return () => {
      resetField(inputFieldName);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  return (
    <div className={cn('m-0.5 flex flex-col lg:m-0')}>
      <div className="flex items-center justify-between pb-4">
        <h4 className="font-semibold">
          {title}
          {required && (
            <span data-testid="required">
              {' '}
              <span className="text-red-400">
                (<Trans id="wymagane">wymagane</Trans>)
              </span>
            </span>
          )}
        </h4>
      </div>
      {hint && (
        <Hint variant="primary" className="mb-4 p-4">
          <Hint.Title>{hint.title}</Hint.Title>
          <Hint.Description>{hint.description}</Hint.Description>
        </Hint>
      )}
      <Controller
        rules={{
          validate: () => {
            if (files.some((i) => i.status === STATUS_FILE.UPLOADING))
              return false;
            if (extraError) return extraError;
            return (
              files.some((i) => i.status === STATUS_FILE.FINISHED) || !required
            );
          },
        }}
        name={inputFieldName}
        render={({ fieldState }) => (
          <UploaderDropzone
            variant={
              disabled
                ? 'disabled'
                : fieldState.error || error || extraError
                  ? 'error'
                  : 'default'
            }
            fileButton={
              <ButtonFile
                className="absolute inset-0 size-full cursor-pointer rounded-[10px] opacity-0"
                text={' '}
                extraProps={{
                  'data-testid': `uploader-${inputFieldName}`,
                  'aria-label': i18n.t('Wybierz plik lub przeciągnij go tutaj'),
                }}
              />
            }
          />
        )}
      />
      {files.length > 0 && (
        <div className="mt-4 flex flex-col gap-2" ref={parent}>
          {isLoading && (
            <>
              <Skeleton className={'h-[102px] w-full lg:h-[68px]'} />
              <Skeleton className={'h-[102px] w-full lg:h-[68px]'} />
            </>
          )}
          {files.map((item) => (
            <Fragment key={item.name + item.size}>
              <FileItem
                item={item}
                handleDeleteFile={handleDeleteFile}
                handleRetry={handleRetry}
                handleUpdatePassword={handleUpdatePassword}
                attachmentTypeId={attachmentTypeId}
              />
            </Fragment>
          ))}
        </div>
      )}
      {error && (
        <Hint
          color="red"
          className={cn('mb-0 mt-4', files.length > 0 && 'mt-2')}
        >
          {error}
        </Hint>
      )}
      {extraError && (
        <Hint
          color="red"
          className={cn('mb-0 mt-4', files.length > 0 && 'mt-2')}
        >
          {extraError}
        </Hint>
      )}
      {warning && (
        <Hint color="yellow" className="mb-0 mt-2">
          {warning}
        </Hint>
      )}
      <div className="mt-4">
        <Instruction {...instructionProps} />
      </div>
    </div>
  );
};
